import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';
import API_ENDPOINTS from '../../apiEndpoints';

// Types for IFSC API
export interface IFSCResponse {
  bankName: string;
  ifsc: string;
  branchName: string;
  swift: string;
}

export interface IFSCRequest {
  ifsc: string;
}

export const ifscFetchApi = createApi({
  reducerPath: 'ifscFetch',
  baseQuery: axiosBaseQuery,
  tagTypes: ['IFSC'],
  endpoints: (builder) => ({
    fetchIFSC: builder.query<IFSCResponse, string>({
      query: (ifsc: string) => ({
        url: API_ENDPOINTS.REACT_APP_IFSC_FETCH,
        method: 'GET',
        data: undefined,
        params: { ifsc },
      }),
      keepUnusedDataFor: Infinity, // Cache for infinite time
      providesTags: (result, error, ifsc) => [{ type: 'IFSC', id: ifsc }],
      transformResponse: (response: any) => {
        // Clean up branch name if it contains quotes
        if (response.branchName && response.branchName.includes('"')) {
          response.branchName = response.branchName.replace('"', '');
        }
        return response;
      },
      // Custom cache management to keep only last 5 queries
      onQueryStarted: async (ifsc, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled;
          
          // Get current state to access the cache
          const state = getState() as any;
          const cache = state.ifscFetch?.queries;
          
          if (cache) {
            const queryKeys = Object.keys(cache);
            
            // If we have more than 5 cached queries, remove the oldest ones
            if (queryKeys.length > 5) {
              const sortedKeys = queryKeys.sort((a, b) => {
                const aTime = cache[a]?.fulfilledTimeStamp || 0;
                const bTime = cache[b]?.fulfilledTimeStamp || 0;
                return aTime - bTime; // Sort by timestamp, oldest first
              });
              
              // Remove the oldest queries (keep only the last 5)
              const keysToRemove = sortedKeys.slice(0, queryKeys.length - 5);
              
              keysToRemove.forEach((key) => {
                dispatch(ifscFetchApi.util.invalidateTags([{ type: 'IFSC', id: key }]));
              });
            }
          }
        } catch (error) {
          // Handle error if needed
          console.error('IFSC query failed:', error);
        }
      },
    }),
  }),
  // Custom cache management
  keepUnusedDataFor: Infinity,
});

// Export hooks for usage in functional components
export const { useFetchIFSCQuery, useLazyFetchIFSCQuery } = ifscFetchApi;

// Export the API for store configuration
export default ifscFetchApi;
